"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { debounce } from "es-toolkit";
import { ChevronDownIcon, LayoutDashboardIcon } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import AdsIcon from "@/assets/icons/ads.svg";
import IwIcon from "@/assets/icons/iw.svg";
import SaIcon from "@/assets/icons/sa.svg";
import UaIcon from "@/assets/icons/ua.svg";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  getProjectGroupsStateCookie,
  setProjectGroupsStateCookie,
} from "@/lib/cookie/cookie-project-groups";
import { cn } from "@/lib/utils";
import { useSidebarSearchGroupMatch } from "./sidebar-search-context";
import type { CollapsibleState, ProjectGroupType } from "./types";

type Props = {
  groupType: ProjectGroupType;
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  initialState: CollapsibleState;
  disableActionTooltip?: boolean;
};

// ツールチップメッセージの定義
const tooltipMessages: Partial<
  Record<ProjectGroupType, ReturnType<typeof msg>>
> = {
  dashboards: msg`Create new dashboard`,
  sa: msg`Create new Search Analytics project`,
  ua: msg`Create new User Analytics project`,
  iw: msg`Create new Index Worker project`,
  ads: msg`Create new Ads Analytics project`,
};

/**
 * 折りたたみ可能なプロジェクトグループのコンポーネント
 */
export default function CollapsibleProjectGroup({
  groupType,
  title,
  action,
  children,
  initialState,
  disableActionTooltip = false,
}: Props) {
  const { i18n } = useLingui();
  const { isFiltering, isVisible } = useSidebarSearchGroupMatch(groupType);

  // サーバー側で決定した初期状態を使用（ハイドレーション対策）
  const [open, setOpen] = useState(Boolean(initialState[groupType]));

  // グループタイプに応じたツールチップメッセージを取得
  const tooltipMessage = tooltipMessages[groupType]
    ? i18n._(tooltipMessages[groupType])
    : undefined;

  // デバウンス付きクッキー保存
  const debouncedSaveCookie = useMemo(
    () =>
      debounce((groupType: ProjectGroupType, newOpen: boolean) => {
        const currentState = getProjectGroupsStateCookie();
        const newState: CollapsibleState = {
          sa: currentState?.sa ?? initialState.sa ?? false,
          ua: currentState?.ua ?? initialState.ua ?? false,
          iw: currentState?.iw ?? initialState.iw ?? false,
          ads: currentState?.ads ?? initialState.ads ?? false,
          dashboards:
            currentState?.dashboards ?? initialState.dashboards ?? false,
          [groupType]: newOpen,
        };
        setProjectGroupsStateCookie(newState);
      }, 500),
    [initialState],
  );

  // 開閉状態が変更されたときの処理
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen); // UIは即座に更新
    debouncedSaveCookie(groupType, newOpen); // クッキー保存はデバウンス
  };

  return (
    <Collapsible
      open={isFiltering ? true : open}
      onOpenChange={isFiltering ? undefined : handleOpenChange}
      className={cn(
        "group/group-collapsible",
        // 非一致 group の子 project も mounted のまま保ち、検索前の状態を復元する。
        isFiltering && !isVisible && "hidden",
      )}
    >
      <SidebarGroup className="group/sidebar-group relative !px-0 !py-2">
        <SidebarGroupLabel className="relative flex items-center !px-0 !py-0 !h-7 w-full min-w-0 sticky top-[var(--sidebar-sticky-top,0px)] z-20 bg-sidebar before:absolute before:inset-x-0 before:top-[calc(-1*var(--sidebar-sticky-top,0px))] before:h-[var(--sidebar-sticky-top,0px)] before:bg-sidebar before:content-[''] before:pointer-events-none">
          <CollapsibleTrigger className="flex items-center gap-1 flex-1 cursor-pointer hover:bg-sidebar-accent/50 rounded transition-colors min-w-0 px-2 h-7 group/trigger">
            <div className="relative size-4 shrink-0 flex">
              {groupType === "iw" ? (
                <>
                  <IwIcon className="absolute inset-0 size-4 transition-opacity duration-75 group-hover/trigger:opacity-0" />
                  <ChevronDownIcon className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inset-0 size-3 transition-all duration-75 opacity-0 group-hover/trigger:opacity-100 group-data-[state=closed]/group-collapsible:-rotate-90" />
                </>
              ) : groupType === "sa" ? (
                <>
                  <SaIcon className="absolute inset-0 size-4 transition-opacity duration-75 group-hover/trigger:opacity-0" />
                  <ChevronDownIcon className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inset-0 size-3 transition-all duration-75 opacity-0 group-hover/trigger:opacity-100 group-data-[state=closed]/group-collapsible:-rotate-90" />
                </>
              ) : groupType === "ua" ? (
                <>
                  <UaIcon className="absolute inset-0 size-4 transition-opacity duration-75 group-hover/trigger:opacity-0" />
                  <ChevronDownIcon className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inset-0 size-3 transition-all duration-75 opacity-0 group-hover/trigger:opacity-100 group-data-[state=closed]/group-collapsible:-rotate-90" />
                </>
              ) : groupType === "dashboards" ? (
                <>
                  <LayoutDashboardIcon className="absolute inset-0 size-4 transition-opacity duration-75 group-hover/trigger:opacity-0" />
                  <ChevronDownIcon className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inset-0 size-3 transition-all duration-75 opacity-0 group-hover/trigger:opacity-100 group-data-[state=closed]/group-collapsible:-rotate-90" />
                </>
              ) : groupType === "ads" ? (
                <>
                  <AdsIcon className="absolute inset-0 size-4 transition-opacity duration-75 group-hover/trigger:opacity-0" />
                  <ChevronDownIcon className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 inset-0 size-3 transition-all duration-75 opacity-0 group-hover/trigger:opacity-100 group-data-[state=closed]/group-collapsible:-rotate-90" />
                </>
              ) : null}
            </div>
            <span className="truncate min-w-0">{title}</span>
          </CollapsibleTrigger>

          {action && (
            <>
              {tooltipMessage ? (
                <Tooltip
                  disableHoverableContent
                  open={disableActionTooltip ? false : undefined}
                >
                  <TooltipTrigger asChild>
                    <div className="ml-auto mr-1.5 flex items-center justify-center h-7 w-7 [&>*]:static [&>*]:top-0 [&>*]:right-0 [&>*]:inset-auto [&>*]:transform-none [&>*]:w-6 [&>*]:h-6 [&>*]:p-0 [&>*]:bg-transparent [&>*]:shadow-none [&>*]:border-none [&>*]:hover:bg-sidebar-accent/30 [&>*]:rounded [&>*]:text-sidebar-foreground/70 [&>*]:hover:text-sidebar-foreground/90 [&_svg]:w-3.5 [&_svg]:h-3.5 [&>*]:flex [&>*]:items-center [&>*]:justify-center">
                      {action}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{tooltipMessage}</TooltipContent>
                </Tooltip>
              ) : (
                <div className="ml-auto mr-1.5 flex items-center justify-center h-7 w-7 [&>*]:static [&>*]:top-0 [&>*]:right-0 [&>*]:inset-auto [&>*]:transform-none [&>*]:w-6 [&>*]:h-6 [&>*]:p-0 [&>*]:bg-transparent [&>*]:shadow-none [&>*]:border-none [&>*]:hover:bg-sidebar-accent/30 [&>*]:rounded [&>*]:text-sidebar-foreground/70 [&>*]:hover:text-sidebar-foreground/90 [&_svg]:w-3.5 [&_svg]:h-3.5 [&>*]:flex [&>*]:items-center [&>*]:justify-center">
                  {action}
                </div>
              )}
            </>
          )}
        </SidebarGroupLabel>

        <CollapsibleContent>
          <SidebarGroupContent
            className={cn(
              "border-l border-sidebar-border pl-5 pr-2",
              "[&_[data-sidebar=menu-sub]]:ml-2 [&_[data-sidebar=menu-sub]]:pl-2.5",
              "[&_[data-sidebar=menu-sub]]:pr-1.5",
              "[&_[data-sidebar=menu-sub]]:translate-x-0",
              "[&_[data-sidebar=menu-button]]:ml-[-4px]",
            )}
          >
            {children}
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
